import * as service from "../../service";
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Overlay } from "../../components/Overlay";
import { LANGUAGE } from "_constants";
import { ICONS, MESSAGES, SECONDARY_DICT } from "./constants";
import { FootballStatusRenderer } from "./components/StatusRenderer";
import { Timer } from "./components/Timer";
import { Ball } from "./components/Ball";
import { AttackIndicator } from "./indicators/AttackIndicator";
import { ThrowInIndicator } from "./indicators/ThrowInIndicator";
import { CornerIndicator } from "./indicators/CornerIndicator";
import { FreeKickIndicator } from "./indicators/FreeKickIndicator";
import { GoalKickIndicator } from "./indicators/GoalKickIndicator";
import { OffsideIndicator } from "./indicators/OffsideIndicator";
import { adjustBallPosition } from "./adjust-ball-position";
import { ScoreIndicator } from "./indicators/ScoreIndicator";
import { Stats } from "./statistics";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import FootballTimeline from "./timeline";
import Lineup from "./lineup";
import GoalRightSvg from "./assets/kale-sag.svg";
import GoalLeftSvg from "./assets/kale-sol.svg";
import AdBannerSvg from "./assets/ad-banner-geniuscom.svg";
import AdDisplay from "../../components/common/AdDisplay";
import GeniusFeeds from "../../assets/ads/genius-logo.svg";
import FastGamesApi from "../../assets/ads/fastgamesapi.svg";
import Monetria from "../../assets/ads/monetria.svg";
import PlayerStats from "./playerStats";
import PreMatchIntro from "../../components/PreMatchIntro";
import Standings from "./standings";
import { ActivePage, pageToTab } from "./utils";
import XgMap from "./xgMap";
import Chalkboard from "./chalkboard";
import { mockEvents } from "./chalkboard/mockChalkboardData";
import { mapRawEventToTimelineEvent } from "./timeline/util";
import { SportTabs } from "components/common/SportTabs";
import FootballStadium from "./assets/football-bg.png";
import FootballCourt from "./assets/football-court.svg";
import PassMap from "./passMap";
import HeatMap from "./heatmap";
import {
  AdBanner,
  FlatLayer,
  FullHeightTabPanel,
  classNames,
  createBackgroundComponent,
} from "../../components/common";

export type FootballProps = {
  status: service.MatchStatus;
  language?: LANGUAGE;
};

const StyledCourt = createBackgroundComponent({
  backgroundImage: FootballStadium,
  baseClassName:
    "w-full h-full bg-center bg-cover flex items-center justify-center relative overflow-hidden",
  displayName: "FootballStyledCourt",
});

const Court = createBackgroundComponent({
  backgroundImage: FootballCourt,
  baseClassName: "absolute inset-0 w-full h-full bg-center bg-cover",
  displayName: "FootballCourt",
});

const GoalImage = ({
  className,
  ...rest
}: React.ImgHTMLAttributes<HTMLImageElement>) => (
  <img
    className={classNames(
      "absolute top-[48%] max-w-[4vw] h-auto z-[999]",
      className
    )}
    alt="goal-image"
    {...rest}
  />
);

const LeftGoal = (props: React.ImgHTMLAttributes<HTMLImageElement>) => (
  <GoalImage className={classNames("left-[6%]", props.className)} {...props} />
);

const RightGoal = (props: React.ImgHTMLAttributes<HTMLImageElement>) => (
  <GoalImage className={classNames("right-[6%]", props.className)} {...props} />
);

export const Football: FC<FootballProps> = memo(
  ({ status, language = LANGUAGE.ENGLISH }) => {
    adjustBallPosition(status);

    const [selectedPage, setSelectedPage] = useState<ActivePage>(
      ActivePage.Narration
    );

    const hasPlayerStats = !!(
      ((status as any)?.lp?.h?.ply &&
        Object.keys((status as any).lp.h.ply).length > 0) ||
      ((status as any)?.lp?.a?.ply &&
        Object.keys((status as any).lp.a.ply).length > 0) ||
      ((status as any)?.c?.lp?.h?.ply &&
        Object.keys((status as any).c.lp.h.ply).length > 0) ||
      ((status as any)?.c?.lp?.a?.ply &&
        Object.keys((status as any).c.lp.a.ply).length > 0)
    );

    const hasMatchTimeline = useMemo(() => {
      const raw = (status as any)?.m;
      if (!Array.isArray(raw) || raw.length === 0) return false;
      for (const item of raw) {
        const mapped = mapRawEventToTimelineEvent(item);
        if ((mapped.minute ?? 0) > 0 || !!mapped.team) return true;
      }
      return false;
    }, [status]);
    const hasLineup = !!(
      ((status as any)?.lp?.h?.ply &&
        Object.keys((status as any).lp.h.ply).length > 0) ||
      ((status as any)?.lp?.a?.ply &&
        Object.keys((status as any).lp.a.ply).length > 0) ||
      ((status as any)?.c?.lp?.h?.ply &&
        Object.keys((status as any).c.lp.h.ply).length > 0) ||
      ((status as any)?.c?.lp?.a?.ply &&
        Object.keys((status as any).c.lp.a.ply).length > 0)
    );
    const hasStandings =
      !!(status as any)?.lt && Object.keys((status as any).lt || {}).length > 0;

    const tabNames = useMemo(
      () => ({
        [ActivePage.Narration]: "Narration",
        [ActivePage.Statistics]: "Statistics",
        [ActivePage.PlayerStats]: hasPlayerStats ? "Player Stats" : null,
        [ActivePage.Match]: hasMatchTimeline ? "Match" : null,
        [ActivePage.Lineup]: hasLineup ? "Lineup" : null,
        [ActivePage.Standings]: hasStandings ? "Standings" : null,
        [ActivePage.XgMap]: "XG Map",
        [ActivePage.PassMap]: "Pass Map",
        [ActivePage.HeatMap]: "Heatmap",
        [ActivePage.Chalkboard]: "Chalkboard",
      }),
      [hasPlayerStats, hasMatchTimeline, hasLineup, hasStandings]
    );

    const availableTabs = useMemo(
      () =>
        [
          ActivePage.Narration,
          ActivePage.Statistics,
          ActivePage.PlayerStats,
          ActivePage.Match,
          ActivePage.Lineup,
          ActivePage.Standings,
          ActivePage.XgMap,
          ActivePage.PassMap,
          ActivePage.HeatMap,
          ActivePage.Chalkboard,
        ]
          .filter((page) => tabNames[page])
          .map((page) => ({ key: page, label: tabNames[page] as string })),
      [tabNames]
    );

    const messageGenerator = useCallback(
      ({
        language,
        normalizedStatusCode,
        statusCode,
        statusMessage,
      }: {
        language: LANGUAGE;
        normalizedStatusCode?: string;
        statusCode?: string;
        statusMessage?: string;
      }) => {
        if (!normalizedStatusCode) return undefined;
        const langDict = MESSAGES[language];
        if (!langDict) return undefined;
        let message = langDict[normalizedStatusCode];

        if (normalizedStatusCode === "1280") message = statusMessage!;

        const secondaryDict = SECONDARY_DICT[language];

        if (secondaryDict && secondaryDict[message])
          message = secondaryDict[message];

        const icon = ICONS[normalizedStatusCode];
        return { message: message?.toUpperCase(), icon };
      },
      []
    );

    const isPreMatch = useMemo(() => {
      const stsVal = status?.sts as unknown;
      const stsNum =
        typeof stsVal === "number"
          ? stsVal
          : typeof stsVal === "string"
          ? Number(stsVal)
          : NaN;

      const notStartedByStatus = !Number.isNaN(stsNum) && stsNum === 0;

      const tmVal = status?.tm as unknown;
      const tmNum =
        typeof tmVal === "number"
          ? tmVal
          : typeof tmVal === "string"
          ? Number(tmVal)
          : NaN;

      const notStartedByTime = !Number.isNaN(tmNum) && tmNum <= 0;

      const noLiveNsc = !status?.nsc; // no live normalized status code yet
      return (notStartedByStatus || notStartedByTime) && noLiveNsc;
    }, [status]);

    useEffect(() => {
      const handler = (ev: MessageEvent) => {
        const data = ev.data;
        if (!data || typeof data !== "object") return;
        if (data.type === "REQUEST_TAB") {
          window.parent?.postMessage(
            { type: "TAB_CHANGED", tab: pageToTab(selectedPage) },
            "*"
          );
        }
      };
      window.addEventListener("message", handler);
      return () => window.removeEventListener("message", handler);
    }, [selectedPage]);

    const handleSelectPage = (p: ActivePage) => {
      setSelectedPage(p);
      window.parent?.postMessage(
        { type: "TAB_CHANGED", tab: pageToTab(p) },
        "*"
      );
    };

    return (
      <>
        <SportTabs
          tabs={availableTabs}
          activeKey={selectedPage}
          onChange={(key) => handleSelectPage(key as ActivePage)}
        />

        <Carousel
          showThumbs={false}
          selectedItem={selectedPage}
          autoPlay={false}
          emulateTouch
          showStatus={false}
          showArrows={false}
          showIndicators={false}
          swipeable={false}
          onChange={(e) => handleSelectPage(e as ActivePage)}
        >
          <StyledCourt className="styled-court">
            <Timer status={status} />
            <AdBanner src={AdBannerSvg} alt="Ad Banner" />
            <AdDisplay adImages={[GeniusFeeds, FastGamesApi, Monetria]} />
            <LeftGoal src={GoalLeftSvg} alt="Left Goal" />

            <Overlay
              status={status}
              messageGenerator={messageGenerator}
              language={language}
              StatusRenderer={FootballStatusRenderer}
            >
              <Court />

              {!isPreMatch && (
                <>
                  <AttackIndicator status={status} />
                  <ThrowInIndicator status={status} />
                  <CornerIndicator status={status} />
                  <FreeKickIndicator status={status} />
                  <GoalKickIndicator status={status} />
                  <OffsideIndicator status={status} />
                  <Ball status={status} />
                </>
              )}
            </Overlay>

            {(isPreMatch || status?.nsc === "1233") && (
              <FlatLayer>
                <PreMatchIntro status={status} />
              </FlatLayer>
            )}

            <RightGoal src={GoalRightSvg} alt="Right Goal" />
            {!isPreMatch && <ScoreIndicator status={status} />}
          </StyledCourt>

          <FullHeightTabPanel>
            <Stats status={status} language={language} />
          </FullHeightTabPanel>

          {hasPlayerStats && (
            <FullHeightTabPanel>
              <PlayerStats status={status} language={language} />
            </FullHeightTabPanel>
          )}

          {hasMatchTimeline && (
            <FullHeightTabPanel>
              <FootballTimeline status={status} />
            </FullHeightTabPanel>
          )}

          {hasLineup && (
            <FullHeightTabPanel>
              <Lineup status={status} />
            </FullHeightTabPanel>
          )}

          {hasStandings && (
            <FullHeightTabPanel>
              <Standings status={status} />
            </FullHeightTabPanel>
          )}

          <FullHeightTabPanel>
            <XgMap status={status} />
          </FullHeightTabPanel>

          <FullHeightTabPanel>
            <PassMap status={status} />
          </FullHeightTabPanel>

          <FullHeightTabPanel>
            <HeatMap status={status} />
          </FullHeightTabPanel>

          <FullHeightTabPanel>
            <Chalkboard status={status} events={mockEvents} />
          </FullHeightTabPanel>
        </Carousel>
      </>
    );
  }
);

export { StyledCourt, Overlay };
